import React, {MouseEvent, ReactNode, useContext, useRef} from 'react'

import t from '../../../helpers/Languages.js'
import Popover, {PopoverRef} from '../overlays/Popover.js'
import ListItem from '../surfaces/ListItem.js'
import FormRowContext, {FieldRowReset, getFieldRowOffset, markFieldRowControl} from '../forms/FormRowContext.js'
import Button, {ButtonProps} from './Button.js'
import {buttonSizes, getSplitRadius} from './styles.js'

export interface SplitButtonOption {
    title?: string
    value?: string
    /** Called instead of onChange when this option is clicked */
    action?: (event: MouseEvent) => void
}

/** What onChange gets: the value of the clicked option, at the place callers read it from */
export interface SplitButtonChangeEvent {
    target: {value: string}
}

export interface SplitButtonProps extends Omit<ButtonProps, 'onChange' | 'afterIcon' | 'buttonStyle'> {
    options?: SplitButtonOption[]
    /** The option shown as active in the menu */
    value?: string
    /** Called with the clicked option's value, for an option without action */
    onChange?: (event: SplitButtonChangeEvent) => void
}

/**
 * A Button (the main action, onClick) with an arrow at its right that opens a menu of options. Clicking an option
 * calls its action, or onChange with its value. The style prop places the whole button, the main part takes the room
 * the arrow leaves.
 */
function SplitButton({options, value, onChange, style = {}, variant, color, size = 'md', disabled, loading, children, ...rest}: SplitButtonProps) {
    const popoverRef = useRef<PopoverRef>(null)
    const inTopAlignedRow = useContext(FormRowContext)

    const height = typeof style.height === 'number' ? style.height : buttonSizes.height[size]

    // The offset goes on the outer element, unless the caller placed the button itself
    const rowOffset = style.marginTop === undefined && style.margin === undefined ? getFieldRowOffset(inTopAlignedRow, height) : {}

    // The main part fills the width the caller gives the whole button
    const fills = style.width !== undefined || style.flex !== undefined

    const onClickOption = (option: SplitButtonOption, event: MouseEvent) => {
        event.stopPropagation()

        if (typeof option.action === 'function') {
            option.action(event)
        } else {
            onChange?.({target: {value: option.value}})
        }

        popoverRef.current?.close()
    }

    return (
        <div style={{display: 'flex', height, width: fills ? undefined : 'fit-content', ...rowOffset, ...style}}>
            {/* The buttons inside are placed by this element, so they don't get an offset of their own */}
            <FieldRowReset>
                <Button
                    {...rest}
                    variant={variant}
                    color={color}
                    size={size}
                    disabled={disabled}
                    loading={loading}
                    style={{height: '100%', ...(fills && {flex: 1, minWidth: 0})}}
                    buttonStyle={{borderRadius: getSplitRadius('left'), paddingRight: buttonSizes.splitInnerPadding, borderRight: 'none'}}
                >
                    {children as ReactNode}
                </Button>

                <Popover
                    ref={popoverRef}
                    disabled={disabled || loading}
                    content={
                        <>
                            {options?.map((option, index) => (
                                <ListItem
                                    key={`${index}button`}
                                    size='sm'
                                    active={typeof option.value === 'string' && value === option.value}
                                    onClick={(event: MouseEvent) => onClickOption(option, event)}
                                >
                                    {option.title || option.value}
                                </ListItem>
                            ))}
                        </>
                    }
                >
                    {/* While loading the arrow keeps the colour of the main part (a disabled arrow would be grey next to
                        it), it just doesn't open the menu */}
                    <Button
                        aria-label={t('moreOptions')}
                        aria-haspopup='menu'
                        aria-disabled={loading || undefined}
                        variant={variant}
                        color={color}
                        size={size}
                        disabled={disabled}
                        style={{height: '100%'}}
                        buttonStyle={{borderRadius: getSplitRadius('right'), paddingLeft: buttonSizes.splitInnerPadding, paddingRight: buttonSizes.splitArrowPaddingRight, borderLeft: 'none', ...(loading && {cursor: 'default'})}}
                        onClick={(event) => {
                            event.stopPropagation()

                            if (!loading) {
                                popoverRef.current?.show()
                            }
                        }}
                        icon='mdi mdi-menu-down'
                    />
                </Popover>
            </FieldRowReset>
        </div>
    )
}

export default markFieldRowControl(SplitButton)
